import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Lightweight i18n: no external dependency. Each page/component owns its own
// { en, fr } dictionary and reads the active language via useLang()/useT().
const LanguageContext = createContext(null);
const STORAGE_KEY = 'elec_lang';

const SUPPORTED = ['fr', 'en'];

function normalize(value) {
    return SUPPORTED.includes(value) ? value : null;
}

// Stored preference > browser language > French (primary market: Cameroon).
function detectInitial() {
    try {
        const saved = normalize(localStorage.getItem(STORAGE_KEY));
        if (saved) return saved;
    } catch {
        // localStorage unavailable (private mode, SSR) — fall through.
    }
    const nav = ((typeof navigator !== 'undefined' && navigator.language) || 'fr').toLowerCase();
    return nav.startsWith('en') ? 'en' : 'fr';
}

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(detectInitial);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch {
            // ignore persistence failures
        }
        if (typeof document !== 'undefined') {
            document.documentElement.lang = lang;
        }
    }, [lang]);

    const setLang = useCallback((next) => {
        const normalized = normalize(next);
        if (normalized) setLangState(normalized);
    }, []);

    const toggleLang = useCallback(() => {
        setLangState((prev) => (prev === 'en' ? 'fr' : 'en'));
    }, []);

    return (
        <LanguageContext.Provider value={{ lang, setLang, toggleLang }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLang() {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        throw new Error('useLang must be used within a LanguageProvider');
    }
    return ctx;
}

// Convenience hook: pass a { en: {...}, fr: {...} } dictionary, get the active
// language slice back (falls back to English, then French, then {}).
export function useT(dict) {
    const { lang } = useLang();
    return dict[lang] || dict.en || dict.fr || {};
}
